import { useEffect, useState } from "react";
import api from "@/lib/api";
import { dt } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Phone, Instagram, Radio, Plus, Trash2, CheckCircle2, AlertCircle, Send, Copy, PlugZap } from "lucide-react";
import { toast } from "sonner";

const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", icon: Phone, color: "bg-emerald-500" },
  instagram: { label: "Instagram", icon: Instagram, color: "bg-gradient-to-br from-purple-500 to-pink-500" },
};

const ENV_VARS_INFO = [
  { key: "META_APP_ID", label: "App ID de Meta", secreto: false },
  { key: "META_APP_SECRET", label: "App Secret", secreto: true },
  { key: "META_VERIFY_TOKEN", label: "Verify Token", secreto: false },
  { key: "META_GRAPH_VERSION", label: "Versión Graph API", secreto: false },
  { key: "PUBLIC_BASE_URL", label: "URL pública", secreto: false },
  { key: "WHATSAPP_ACCESS_TOKEN", label: "WhatsApp Access Token", secreto: true },
  { key: "WHATSAPP_PHONE_NUMBER_ID", label: "WhatsApp Phone Number ID", secreto: false },
  { key: "WHATSAPP_BUSINESS_ACCOUNT_ID", label: "WhatsApp Business Account ID", secreto: false },
  { key: "IG_PAGE_ID", label: "Instagram Page ID", secreto: false },
  { key: "IG_BUSINESS_ACCOUNT_ID", label: "Instagram Business Account ID", secreto: false },
  { key: "IG_ACCESS_TOKEN", label: "Instagram Access Token", secreto: true },
  // Fix P (2026-10-01): WhatsApp real por YCloud (coexistencia); se muestran solo con YCloud activo
  { key: "YCLOUD_API_KEY", label: "YCloud API Key", secreto: true, ycloud: true },
  { key: "YCLOUD_WEBHOOK_SECRET", label: "YCloud Webhook Secret", secreto: true, ycloud: true },
  { key: "WHATSAPP_BUSINESS_NUMBER", label: "Número de WhatsApp de la tienda", secreto: false, ycloud: true },
];

function CopyRow({ label, value, onCopy, testId }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-mono text-xs truncate" data-testid={`${testId}-value`}>{value || "—"}</p>
      </div>
      <Button size="sm" variant="ghost" className="rounded-full shrink-0" onClick={() => onCopy(value, label)} disabled={!value} data-testid={`${testId}-copy`}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function Channels() {
  const [channels, setChannels] = useState(null);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [testDialog, setTestDialog] = useState(null);
  const [destino, setDestino] = useState("");
  const [texto, setTexto] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [tplForm, setTplForm] = useState(null);
  const [conexion, setConexion] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [probando, setProbando] = useState(null);
  const [suscribiendo, setSuscribiendo] = useState(false);
  const [obteniendoIG, setObteniendoIG] = useState(false);
  const [probarResult, setProbarResult] = useState({});

  const load = async () => {
    const [c, l, t] = await Promise.all([api.get("/channels"), api.get("/channels/webhook-logs"), api.get("/plantillas")]);
    setChannels(c.data); setLogs(l.data); setTemplates(t.data);
  };
  const loadConexion = async () => {
    try { const { data } = await api.get("/channels/conexion"); setConexion(data); }
    catch (e) { toast.error("No se pudo cargar el estado de conexión"); }
  };
  const loadTokenInfo = async () => {
    try { const { data } = await api.get("/channels/token-info"); setTokenInfo(data); }
    catch (e) { setTokenInfo({ valido: false, error: { message: "No se pudo consultar el token." } }); }
  };
  useEffect(() => { load(); loadConexion(); loadTokenInfo(); }, []);

  const copiar = (valor, label) => {
    navigator.clipboard.writeText(valor || "");
    toast.success(`${label} copiado`);
  };

  const probar = async (canal) => {
    setProbando(canal); setProbarResult((p) => ({ ...p, [canal]: null }));
    try {
      const { data } = await api.post(`/channels/${canal}/probar`);
      if (data.ok) {
        setProbarResult((p) => ({ ...p, [canal]: { ok: true, detalle: JSON.stringify(data.data) } }));
        toast.success(`${canal === "whatsapp" ? "WhatsApp" : "Instagram"} respondió correctamente`);
      } else {
        const err = data.error || {};
        let detalle = `${err.tipo === "ycloud" ? "YCloud" : "Meta"} respondió: code=${err.code ?? "?"}, error_subcode=${err.error_subcode ?? "?"} — ${err.message || "Error desconocido"}`;
        if (err.code === 190) detalle += " · El token venció o fue revocado. Hay que cargar uno nuevo en WHATSAPP_ACCESS_TOKEN.";
        setProbarResult((p) => ({ ...p, [canal]: { ok: false, detalle } }));
        toast.error(`Falló la prueba de ${canal === "whatsapp" ? "WhatsApp" : "Instagram"}`);
      }
    } catch (e) {
      setProbarResult((p) => ({ ...p, [canal]: { ok: false, detalle: e.response?.data?.detail || "Error desconocido" } }));
      toast.error(`Falló la prueba de ${canal === "whatsapp" ? "WhatsApp" : "Instagram"}`);
    } finally { setProbando(null); }
  };

  const obtenerTokenIG = async () => {
    setObteniendoIG(true);
    try {
      const { data } = await api.post("/channels/instagram/obtener-token");
      if (data.ok) { toast.success(`Token de Instagram obtenido${data.pagina ? `: ${data.pagina}` : ""}`); loadConexion(); load(); }
      else toast.error(data.error?.message || "No se pudo obtener el token de Instagram");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al obtener el token"); }
    finally { setObteniendoIG(false); }
  };

  const suscribir = async () => {
    setSuscribiendo(true);
    try {
      const { data } = await api.post("/channels/suscribir");
      if (data.errores?.length) data.errores.forEach((e) => toast.error(e));
      else if (data.resultados?.whatsapp?.data?.detalle) toast.success(`Instagram suscrito. ${data.resultados.whatsapp.data.detalle}`); // Fix P
      else toast.success("Webhooks suscritos en ambos canales");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al suscribir"); }
    finally { setSuscribiendo(false); }
  };

  const setModo = async (canal, modo) => {
    try { await api.put(`/channels/${canal}/modo`, { modo }); toast.success("Modo actualizado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openTest = (canal) => { setTestDialog(canal); setDestino(""); setTexto(""); setTestResult(null); };
  const sendTest = async () => {
    setSending(true); setTestResult(null);
    try {
      const { data } = await api.post(`/channels/${testDialog}/test-message`, { destino, texto });
      // Fix O (2026-09-24): si Meta no acepta el envío, llega ok=false con el error real y una pista.
      if (data.ok === false) {
        const err = data.error || {};
        const cod = err.code ? ` (código ${err.code}${err.error_subcode ? ` / ${err.error_subcode}` : ""})` : "";
        setTestResult({ ok: false, detalle: `${err.message || "Meta no aceptó el envío"}${cod}`, pista: err.pista || "" });
        if (err.tipo === "demora") toast.warning("Meta no respondió a tiempo: revisá si el mensaje llegó antes de reintentar");
        else if (err.tipo === "conexion") toast.error("No se pudo completar el envío de prueba");
        else toast.error("Meta no aceptó el mensaje de prueba");
        return;
      }
      setTestResult({ ok: true, detalle: JSON.stringify(data.resultado) });
      toast.success("Meta aceptó el mensaje");
    } catch (e) {
      const d = e.response?.data?.detail;
      const st = e.response?.status;
      setTestResult({ ok: false, detalle: typeof d === "string" ? d : `No llegó una respuesta del servidor${st ? ` (HTTP ${st})` : ""}. Probá de nuevo en un minuto.` });
      toast.error("Falló el envío de prueba");
    } finally { setSending(false); }
  };

  const saveTpl = async () => {
    try {
      const body = { ...tplForm, variables: (tplForm.variables_str || "").split(",").map((v) => v.trim()).filter(Boolean) };
      delete body.variables_str;
      if (tplForm.id) await api.put(`/plantillas/${tplForm.id}`, body);
      else await api.post("/plantillas", body);
      toast.success("Plantilla guardada"); setTplForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const delTpl = async (t) => { await api.delete(`/plantillas/${t.id}`); load(); };

  if (!channels) return <div className="p-8"><Loader /></div>;

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      <PageHeader title="Canales" subtitle="WhatsApp e Instagram, conectados a la misma bandeja y al mismo agente." />
      <Tabs defaultValue="estado">
        <TabsList className="rounded-full mb-4 flex-wrap h-auto">
          <TabsTrigger value="estado" className="rounded-full gap-1" data-testid="tab-canal-estado"><Radio className="h-4 w-4" /> Estado</TabsTrigger>
          <TabsTrigger value="conexion" className="rounded-full gap-1" data-testid="tab-canal-conexion"><PlugZap className="h-4 w-4" /> Conexión</TabsTrigger>
          <TabsTrigger value="plantillas" className="rounded-full gap-1" data-testid="tab-canal-plantillas">Plantillas</TabsTrigger>
          <TabsTrigger value="registro" className="rounded-full gap-1" data-testid="tab-canal-registro">Registro de webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="estado">
          <div className="space-y-3">
            {channels.map((c) => {
              const meta = CHANNEL_META[c.canal];
              return (
                <div key={c.canal} className="ms-card p-5" data-testid={`channel-card-${c.canal}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className={`h-11 w-11 rounded-2xl ${meta.color} flex items-center justify-center shrink-0`}>
                      <meta.icon className="h-5 w-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-[140px]">
                      <p className="font-head font-semibold">{meta.label}</p>
                      {c.configurado ? (
                        <Badge className="rounded-full border-0 bg-emerald-50 text-emerald-600 gap-1 mt-1" data-testid={`channel-connected-${c.canal}`}><CheckCircle2 className="h-3 w-3" /> Conectado</Badge>
                      ) : (
                        <Badge className="rounded-full border-0 bg-amber-50 text-amber-600 gap-1 mt-1" data-testid={`channel-pending-${c.canal}`}><AlertCircle className="h-3 w-3" /> Pendiente de conexión</Badge>
                      )}
                    </div>
                    <Select value={c.modo} onValueChange={(v) => setModo(c.canal, v)}>
                      <SelectTrigger className="rounded-xl w-44" data-testid={`channel-modo-${c.canal}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agente_activo">Agente activo</SelectItem>
                        <SelectItem value="solo_recibir">Solo recibir</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Fix O (2026-09-24): qué hace cada modo */}
                  <p className="text-xs text-muted-foreground mt-3" data-testid={`channel-modo-ayuda-${c.canal}`}>
                    {c.modo === "solo_recibir"
                      ? "Solo recibir: los mensajes llegan al panel, pero el sistema no escribe solo (ni el agente, ni seguimientos, ni avisos o recordatorios de pago). Lo que mande una persona desde el panel sí sale."
                      : "Agente activo: el agente responde solo y salen los mensajes automáticos (seguimientos, avisos y recordatorios de pago)."}
                  </p>
                  {/* Fix P (2026-10-01): por dónde está conectado WhatsApp */}
                  {c.canal === "whatsapp" && (
                    <p className="text-xs text-muted-foreground mt-2" data-testid="channel-proveedor-whatsapp">
                      {c.proveedor === "ycloud"
                        ? "Número real de la tienda, conectado por YCloud con coexistencia: la app WhatsApp Business sigue andando en el celular. Lo que se responde desde el celular aparece acá como «Celular de la tienda» y esa conversación pasa a una persona (el agente la retoma con «Reactivar agente»). Apagá en la app los mensajes automáticos (bienvenida y ausencia): cada uno cuenta como respuesta desde el celular."
                        : "Conexión directa con Meta (número de prueba)."}
                    </p>
                  )}
                  {!c.configurado && (
                    <p className="text-xs text-muted-foreground mt-3">Faltan variables de entorno: <span className="font-mono">{c.campos_faltantes.join(", ")}</span>. Ver {c.proveedor === "ycloud" ? "README_YCLOUD.md" : "README_META.md"}.</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Último webhook: {c.ultimo_webhook ? `${dt(c.ultimo_webhook.created_at)} · ${c.ultimo_webhook.resultado}` : "todavía no llegó ninguno"}
                  </p>
                  <Button size="sm" variant="outline" className="rounded-full mt-3 gap-1" onClick={() => openTest(c.canal)} data-testid={`test-message-${c.canal}`}>
                    <Send className="h-3.5 w-3.5" /> Enviar mensaje de prueba
                  </Button>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="conexion">
          <div className="space-y-4">
            <div className="ms-card p-5" data-testid="token-whatsapp-card">
              <p className="font-head font-semibold mb-3">{conexion?.proveedor_whatsapp === "ycloud" ? "Token de Meta (lo usa Instagram)" : "Token de WhatsApp"}</p>
              {tokenInfo?.valido ? (
                <div className="space-y-2 text-sm">
                  <p>Tipo: <span className="font-mono" data-testid="token-tipo">{tokenInfo.tipo || "—"}</span></p>
                  <p>Vence: <span className="font-mono" data-testid="token-vence">{tokenInfo.vence}</span></p>
                  <p className="text-xs text-muted-foreground">Permisos: {(tokenInfo.permisos || []).join(", ") || "—"}</p>
                  {(() => {
                    const diasRestantes = tokenInfo.vence !== "nunca" ? (new Date(tokenInfo.vence) - new Date()) / (1000 * 60 * 60 * 24) : Infinity;
                    const esTemporal = tokenInfo.tipo !== "SYSTEM_USER" || diasRestantes < 7;
                    return esTemporal ? (
                      <p className="text-xs rounded-xl p-2 bg-amber-50 text-amber-600" data-testid="token-warning-temporal">Este token es temporal. Reemplazalo por el del usuario del sistema.</p>
                    ) : null;
                  })()}
                  {(() => {
                    const requeridos = ["whatsapp_business_messaging", "whatsapp_business_management"];
                    const faltan = requeridos.filter((p) => !(tokenInfo.permisos || []).includes(p));
                    return faltan.length > 0 ? (
                      <p className="text-xs rounded-xl p-2 bg-red-50 text-red-600" data-testid="token-warning-permisos">Faltan permisos: {faltan.join(", ")}</p>
                    ) : null;
                  })()}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="token-sin-datos">{tokenInfo?.error?.message || "Todavía no se pudo validar el token."}</p>
              )}
            </div>

            <div className="ms-card p-5">
              <p className="font-head font-semibold mb-3">Semáforo de canales</p>
              <div className="flex gap-5 flex-wrap">
                {(channels || []).map((c) => {
                  const semaforo = !c.configurado ? "bg-red-500" : (c.ultimo_webhook?.resultado === "error" ? "bg-amber-500" : "bg-emerald-500");
                  return (
                    <div key={c.canal} className="flex items-center gap-2" data-testid={`semaforo-${c.canal}`}>
                      <span className={`h-3 w-3 rounded-full ${semaforo}`} />
                      <span className="text-sm capitalize">{c.canal}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="ms-card p-5">
              <p className="font-head font-semibold mb-3">URLs de webhook y token</p>
              <div className="space-y-3">
                <CopyRow label="Webhook WhatsApp" value={conexion?.webhook_urls?.whatsapp} testId="url-whatsapp" onCopy={copiar} />
                <CopyRow label="Webhook Instagram" value={conexion?.webhook_urls?.instagram} testId="url-instagram" onCopy={copiar} />
                <CopyRow label="Verify Token" value={conexion?.verify_token} testId="verify-token" onCopy={copiar} />
                {conexion?.proveedor_whatsapp === "ycloud" && (
                  <CopyRow label="Webhook YCloud (WhatsApp real, se pega en YCloud → Developers → Webhooks)" value={conexion?.webhook_urls?.ycloud} testId="url-ycloud" onCopy={copiar} />
                )}
              </div>
            </div>

            <div className="ms-card p-5">
              <p className="font-head font-semibold mb-3">Variables de entorno ({ENV_VARS_INFO.filter((v) => !v.ycloud || conexion?.proveedor_whatsapp === "ycloud").length})</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                {ENV_VARS_INFO.filter((v) => !v.ycloud || conexion?.proveedor_whatsapp === "ycloud").map((v) => {
                  const val = conexion?.env_status?.[v.key];
                  let label, cls;
                  if (v.key === "IG_ACCESS_TOKEN" && typeof val === "string") {
                    if (val === "cargada") { label = "Cargada"; cls = "bg-emerald-50 text-emerald-600"; }
                    else if (val === "obtenida_automaticamente") { label = "Obtenida automáticamente"; cls = "bg-sky-50 text-sky-600"; }
                    else { label = "Falta"; cls = "bg-amber-50 text-amber-600"; }
                  } else {
                    label = val ? "Cargada" : "Falta";
                    cls = val ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600";
                  }
                  return (
                    <div key={v.key} className="flex items-center justify-between text-sm border-b border-border/50 py-1.5" data-testid={`env-status-${v.key}`}>
                      <span>{v.label}{v.secreto && <span className="text-[10px] text-muted-foreground ml-1">(secreto)</span>}</span>
                      <Badge className={`rounded-full border-0 ${cls}`}>{label}</Badge>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="ms-card p-5">
              <p className="font-head font-semibold mb-3">Diagnóstico</p>
              <div className="flex flex-wrap gap-2 mb-3">
                <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => probar("whatsapp")} disabled={probando === "whatsapp"} data-testid="probar-whatsapp"><Phone className="h-3.5 w-3.5" /> Probar WhatsApp</Button>
                <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => probar("instagram")} disabled={probando === "instagram"} data-testid="probar-instagram"><Instagram className="h-3.5 w-3.5" /> Probar Instagram</Button>
                <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={obtenerTokenIG} disabled={obteniendoIG} data-testid="obtener-token-instagram"><Instagram className="h-3.5 w-3.5" /> Obtener token de Instagram</Button>
                <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90 gap-1" onClick={suscribir} disabled={suscribiendo} data-testid="suscribir-webhooks"><PlugZap className="h-3.5 w-3.5" /> Suscribir webhooks</Button>
              </div>
              {["whatsapp", "instagram"].map((canal) => probarResult[canal] && (
                <p key={canal} className={`text-xs rounded-xl p-2 mb-2 ${probarResult[canal].ok ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`} data-testid={`probar-result-${canal}`}>
                  {canal === "whatsapp" ? "WhatsApp" : "Instagram"}: {probarResult[canal].detalle}
                </p>
              ))}
            </div>

            <div className="ms-card p-5">
              <p className="font-head font-semibold mb-3">Últimos 20 webhooks</p>
              {(conexion?.ultimos_webhooks || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Todavía no llegó ningún webhook.</p>
              ) : (
                <div className="space-y-2">
                  {conexion.ultimos_webhooks.map((l) => (
                    <div key={l.id} className="flex items-center justify-between gap-3 text-sm border-b border-border/50 py-1.5" data-testid={`conexion-log-${l.id}`}>
                      <span className="capitalize truncate">{l.canal} · {l.resultado}{l.motivo ? ` (${l.motivo})` : ""}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        {l.valid_hmac === true && <Badge className="rounded-full border-0 bg-emerald-50 text-emerald-600">HMAC válido</Badge>}
                        {l.valid_hmac === false && <Badge className="rounded-full border-0 bg-red-50 text-red-600">HMAC inválido</Badge>}
                        {(l.valid_hmac === null || l.valid_hmac === undefined) && <Badge className="rounded-full border-0 bg-muted text-muted-foreground">N/A</Badge>}
                        <span className="text-[11px] text-muted-foreground">{dt(l.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="plantillas">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setTplForm({ nombre_meta: "", idioma: "es", variables_str: "", cuerpo_ejemplo: "" })} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="new-template"><Plus className="h-4 w-4" /> Nueva plantilla</Button>
          </div>
          {templates.length === 0 ? (
            <EmptyState icon={Radio} title="Sin plantillas" subtitle="Registrá acá las plantillas aprobadas en Meta para usarlas cuando la ventana de 24h esté cerrada." />
          ) : (
            <div className="space-y-2">
              {templates.map((t) => (
                <div key={t.id} className="ms-card p-4 flex items-start justify-between gap-3" data-testid={`template-${t.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{t.nombre_meta} <span className="text-xs text-muted-foreground">· {t.idioma}</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{t.cuerpo_ejemplo}</p>
                    {t.variables?.length > 0 && <p className="text-xs text-muted-foreground mt-1">Variables: {t.variables.join(", ")}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setTplForm({ ...t, variables_str: (t.variables || []).join(", ") })}>Editar</Button>
                    <Button size="sm" variant="ghost" className="rounded-full text-red-500" onClick={() => delTpl(t)} data-testid={`delete-template-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="registro">
          {logs.length === 0 ? <EmptyState icon={Radio} title="Sin webhooks todavía" subtitle="Cuando lleguen eventos de WhatsApp o Instagram vas a verlos acá (últimos 500)." /> : (
            <div className="space-y-2">
              {logs.map((l) => (
                <div key={l.id} className="ms-card p-3 flex items-center justify-between gap-3" data-testid={`webhook-log-${l.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm"><span className="font-semibold capitalize">{l.canal}</span> · {l.direccion} · <span className={l.resultado === "ok" ? "text-emerald-600" : "text-red-500"}>{l.resultado}</span></p>
                    <p className="text-xs text-muted-foreground">{l.tiempo_procesamiento_ms} ms</p>
                  </div>
                  <span className="text-[11px] text-muted-foreground shrink-0">{dt(l.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!testDialog} onOpenChange={(o) => !o && setTestDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mensaje de prueba · {CHANNEL_META[testDialog]?.label}</DialogTitle>
            <DialogDescription>Manda un mensaje real a un destino de prueba para validar que las credenciales cargadas funcionan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>{testDialog === "whatsapp" ? "Número (formato internacional, ej. 18095551234)" : "IGSID del destinatario"}</Label><Input value={destino} onChange={(e) => setDestino(e.target.value)} className="rounded-xl mt-1" data-testid="test-destino" /></div>
            <div><Label>Mensaje</Label><Textarea value={texto} onChange={(e) => setTexto(e.target.value)} className="rounded-xl mt-1" data-testid="test-texto" /></div>
            {testResult && (
              <div className={`text-xs rounded-xl p-2 break-words ${testResult.ok ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`} data-testid="test-result">
                <p>{testResult.detalle}</p>
                {testResult.pista && <p className="mt-1 font-medium" data-testid="test-result-pista">{testResult.pista}</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setTestDialog(null)}>Cerrar</Button>
            <Button onClick={sendTest} disabled={sending || !destino || !texto} className="rounded-full bg-primary hover:bg-primary/90" data-testid="send-test-message">Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tplForm} onOpenChange={(o) => !o && setTplForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{tplForm?.id ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle>
            <DialogDescription>Registrá acá el nombre exacto y las variables de una plantilla ya aprobada en Meta.</DialogDescription>
          </DialogHeader>
          {tplForm && (
            <div className="space-y-3">
              <div><Label>Nombre exacto en Meta</Label><Input value={tplForm.nombre_meta} onChange={(e) => setTplForm({ ...tplForm, nombre_meta: e.target.value })} className="rounded-xl mt-1" data-testid="tpl-nombre" /></div>
              <div><Label>Idioma (código Meta, ej. es, es_DO, en_US)</Label><Input value={tplForm.idioma} onChange={(e) => setTplForm({ ...tplForm, idioma: e.target.value })} className="rounded-xl mt-1" data-testid="tpl-idioma" /></div>
              <div><Label>Variables (separadas por coma, ej. nombre, producto)</Label><Input value={tplForm.variables_str} onChange={(e) => setTplForm({ ...tplForm, variables_str: e.target.value })} className="rounded-xl mt-1" data-testid="tpl-variables" /></div>
              <div><Label>Cuerpo de ejemplo (con {"{nombre}"}, {"{producto}"})</Label><Textarea value={tplForm.cuerpo_ejemplo} onChange={(e) => setTplForm({ ...tplForm, cuerpo_ejemplo: e.target.value })} className="rounded-xl mt-1" data-testid="tpl-cuerpo" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setTplForm(null)}>Cancelar</Button><Button onClick={saveTpl} className="rounded-full bg-primary hover:bg-primary/90" data-testid="tpl-save">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
